import reactUpdate, {Spec} from 'immutability-helper';
import $ from 'jquery';
import moment from 'moment';
import _ from 'underscore';

import {TagSchema} from 'constants/RegistryConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import {writeFragmentsToCache} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {EntryVersion} from 'notebook/ELNConstants';
import * as EntryDayOps from 'notebook/entries/EntryDayOps';
import {
  FromServerTemplate,
  FromServerTemplateVersion,
  Template,
  TEMPLATE_DAY_0_MOMENT,
  TemplateVersion,
  ToServerTemplate,
  ToServerTemplateVersion,
} from 'notebook/entry-templates/TemplateConstants';
import {CidIdMap} from 'notebook/notes/NoteConstants';
import * as NoteStoreTransforms from 'notebook/notes/NoteStoreTransforms';
import {UPDATE_ALL_ITEM_PARTS} from 'notebook/store/auto-save/ItemPartSaveTracker';
// @ts-expect-error
import EntryVersionUtil from 'notebook/util/EntryVersionUtil';
import {NotePart} from 'notebook/util/NoteUtilConstants';
import notifications from 'notifications';
import {TagUpdateObject} from 'schemas/tags/TagUtil';
import pick from 'util/pick';

import TemplateDAO from './TemplateDAO';

const TemplateUtil = {
  /**
   * Transforms the given `op` to operate on the entryVersion of a template.
   */
  entryVersionUpdate(op: Spec<EntryVersion>): Spec<Template> {
    return {templateVersion: {entryVersion: op}};
  },

  removeTasksFromTemplate(template: Template): Template {
    return reactUpdate(
      template,
      TemplateUtil.entryVersionUpdate({
        $apply(entryVersion: EntryVersion): EntryVersion {
          return {...entryVersion, note: NoteStoreTransforms.removeTasksFromNote(entryVersion.note)};
        },
      })
    );
  },

  pickRevertableFields(template: Template): {name: string; templateVersion: TemplateVersion} {
    return pick(template, 'name', 'templateVersion');
  },

  sanitizeTemplate(template: Template, oldTemplate: Template | null = null): Template {
    return reactUpdate(
      template,
      TemplateUtil.entryVersionUpdate({
        $apply(entryVersion: EntryVersion): EntryVersion {
          return EntryVersionUtil.sanitizeEntryVersion(
            entryVersion,
            oldTemplate != null ? oldTemplate.templateVersion.entryVersion : undefined,
            template.id
          );
        },
      })
    );
  },

  prepareForStore(template: FromServerTemplate, cidIdMap: CidIdMap): Template {
    const {
      id,
      name,
      templateCollection,
      editURL,
      permissions,
      latestVersionId,
      archiveRecord,
      tagSchema,
      tags,
      creator,
      creatorId,
      createdAt,
      modifiedAt,
      review,
    } = template;
    return {
      id,
      name,
      templateCollection,
      editURL,
      permissions,
      latestVersionId,
      archiveRecord,
      tagSchema,
      tags,
      creator,
      creatorId,
      createdAt,
      modifiedAt,
      review,
      templateVersion: TemplateUtil.prepareTVForStore(template.templateVersion, cidIdMap, id),
    };
  },

  prepareTVForStore(
    templateVersion: FromServerTemplateVersion,
    cidIdMap: CidIdMap,
    templateId: string
  ): {
    cid: string;
    entryVersion: EntryVersion;
    apiId: string;
  } {
    const cid = _.uniqueId('tv');
    cidIdMap[cid] = templateVersion.id;
    return {
      cid,
      entryVersion: EntryVersionUtil.prepareForStore(templateVersion.entryVersion, cidIdMap, templateId),
      apiId: templateVersion.apiIdentifier,
    };
  },

  prepareTVForSave(
    templateVersion: TemplateVersion,
    cidIdMap: CidIdMap,
    fileId: string,
    unsavedNoteParts: Array<NotePart> | typeof UPDATE_ALL_ITEM_PARTS | undefined
  ): ToServerTemplateVersion {
    return {
      id: cidIdMap[templateVersion.cid] as number,
      cid: templateVersion.cid,
      entryVersion: EntryVersionUtil.prepareForSave(
        templateVersion.entryVersion,
        cidIdMap,
        fileId,
        unsavedNoteParts
      ),
    };
  },

  prepareForSave(
    template: Template,
    cidIdMap: CidIdMap,
    unsavedNoteParts: Array<NotePart> | typeof UPDATE_ALL_ITEM_PARTS | undefined
  ): ToServerTemplate {
    return {
      name: template.name,
      templateVersion: TemplateUtil.prepareTVForSave(
        template.templateVersion,
        cidIdMap,
        template.id,
        unsavedNoteParts
      ),
    };
  },

  createThenOpen(
    name: string,
    templateCollectionId: string,
    isProtected: boolean,
    tagSchema?: TagSchema,
    schemaTags?: TagUpdateObject
  ): JQueryPromise<void> {
    $('#fileLoading').removeClass('hidden');
    return TemplateDAO.create({name, templateCollectionId, isProtected, tagSchema, schemaTags})
      .always(() => {
        $('#fileLoading').addClass('hidden');
      })
      .done((templateResponse: FromServerTemplate) => {
        writeFragmentsToCache(getFragmentWrapper('TemplateSummary'), [
          {
            __typename: 'TemplateNode',
            ...templateResponse.templateVersion.template,
            templateCollection: {
              __typename: 'TemplateCollectionNode',
              ...templateResponse.templateVersion.template.templateCollection,
            },
            editUrl: templateResponse.templateVersion.template.editURL,
            archiveRecord: null,
            creator: {
              __typename: 'UserNode',
              id: templateResponse.creatorId,
              name: templateResponse.creator,
            },
          },
        ]);
        FileBrowserActions.openFile(templateResponse.id, {
          folderItem: {id: templateResponse.id, name: templateResponse.name},
          toolbarTrigger: 'openInformationPanel',
        });
      })
      .fail(notifications.notifyError);
  },

  /*
  Converts an isoDate string into the day number for a template
  E.g., isoDateToTemplateDay('1970-01-06') returns 6
  */
  isoDateToTemplateDay(isoDate: moment.MomentInput): number {
    // oxlint-disable-next-line new-cap
    return moment(isoDate).diff(TEMPLATE_DAY_0_MOMENT(), 'days') + 1;
  },

  /*
  Converts a templateDay number into the corresponding iso-formated date.
  E.g., templateDayToIsoDate(4) returns the string '1970-01-04'
  */
  templateDayToIsoDate(templateDay: number): string {
    // oxlint-disable-next-line new-cap
    return EntryDayOps.formatDate(TEMPLATE_DAY_0_MOMENT().add(templateDay - 1, 'days'));
  },
};

export default TemplateUtil;
